// Cabinet price estimator: API helper, formatting and the cabinet form state

const BASE = '/api/reports/cabinet-estimator';

export async function api(path, { method = 'GET', body, signal } = {}) {
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
  const response = await fetch(`${BASE}${path}`, {
    method,
    signal,
    headers: body && !isForm ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? (isForm ? body : JSON.stringify(body)) : undefined
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) throw new Error(data?.message || `Request failed (${response.status})`);
  return data;
}

const eur = new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const eurCents = new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const number = new Intl.NumberFormat('nl-BE', { maximumFractionDigits: 2 });

export const formatEur = (value) => (value == null || Number.isNaN(Number(value)) ? '—' : eur.format(value));
export const formatEurCents = (value) => (value == null || Number.isNaN(Number(value)) ? '—' : eurCents.format(value));
export const formatNumber = (value) => (value == null || Number.isNaN(Number(value)) ? '—' : number.format(value));

export function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
}

export function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(date);
}

export const IO_LABELS = { di: 'DI', do: 'DO', ai: 'AI', ao: 'AO', fdi: 'F-DI', fdo: 'F-DO' };

export const STARTER_OPTIONS = [
  { label: 'Direct (DOL)', value: 'dol' },
  { label: 'Reversing DOL', value: 'reversing' },
  { label: 'Star-delta', value: 'star_delta' },
  { label: 'Soft starter', value: 'softstarter' },
  { label: 'Frequency drive', value: 'vfd' }
];

export const GROUP_LABELS = {
  enclosure: 'Enclosure & standard kit',
  power: 'Incoming power',
  plc: 'PLC & IO',
  safety: 'Safety',
  motors: 'Motor feeders',
  machine: 'Machine wiring',
  engineering: 'Engineering',
  delivery: 'Delivery'
};

export const CATEGORY_LABELS = {
  enclosure: 'Enclosure',
  enclosure_accessory: 'Enclosure accessory',
  ventilation: 'Ventilation',
  main_switch: 'Main switch',
  busbar_distribution: 'Busbar / distribution',
  circuit_breaker: 'Circuit breaker',
  motor_protection: 'Motor protection',
  contactor: 'Contactor',
  softstarter: 'Soft starter',
  vfd: 'Frequency drive',
  plc_cpu: 'PLC CPU',
  plc_io_module: 'PLC IO module',
  plc_head_station: 'IO head station',
  network: 'Network',
  safety_relay: 'Safety relay',
  power_supply: 'Power supply',
  transformer: 'Transformer',
  pushbutton_signalling: 'Push buttons / signalling',
  signal_tower: 'Signal tower',
  interface_relay: 'Interface relay',
  terminal: 'Terminals',
  cable_wire: 'Cable / wire',
  duct_rail_mounting: 'Duct / rail / mounting',
  small_material: 'Small material',
  weighing: 'Weighing',
  labor_engineering: 'Labour: engineering',
  labor_assembly: 'Labour: assembly',
  labor_site: 'Labour: site',
  transport: 'Transport',
  other: 'Other'
};

export const DOC_KIND_LABELS = {
  cabinet_quote: 'Cabinet quote',
  cabinet_invoice: 'Cabinet invoice',
  time_and_material_invoice: 'Time & material',
  engineering_invoice: 'Engineering',
  installment_invoice: 'Installment',
  credit_note: 'Credit note',
  material_only: 'Material only',
  request_or_list: 'Request / list',
  other: 'Other'
};

let keySeed = 0;
export const newKey = () => `k${Date.now().toString(36)}${(keySeed += 1)}`;

export function newMotor(starter = 'dol', kw = '') {
  return { key: newKey(), name: '', kw: kw === '' ? '' : String(kw), starter, qty: '1' };
}

export function newCabinet(index = 0, overrides = {}) {
  return {
    key: newKey(),
    name: index === 0 ? 'Main cabinet' : `Cabinet ${index + 1}`,
    kind: 'floor',
    widthMm: '',
    incomingA: '',
    plcCpu: index === 0,
    ioStations: '',
    safetyRelays: '',
    weighingPoints: '',
    io: { di: '', do: '', ai: '', ao: '', fdi: '', fdo: '' },
    motors: [],
    ...overrides
  };
}

const toNumber = (value) => {
  if (value === '' || value == null) return null;
  const parsed = Number(String(value).replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
};

// form state -> API spec
export function cabinetToSpec(cabinet) {
  return {
    name: cabinet.name,
    kind: cabinet.kind,
    widthMm: toNumber(cabinet.widthMm),
    incomingA: toNumber(cabinet.incomingA),
    plcCpu: cabinet.plcCpu ? 1 : 0,
    ioStations: toNumber(cabinet.ioStations),
    safetyRelays: toNumber(cabinet.safetyRelays),
    weighingPoints: toNumber(cabinet.weighingPoints) || 0,
    io: Object.fromEntries(Object.keys(IO_LABELS).map((type) => [type, toNumber(cabinet.io[type]) || 0])),
    motors: cabinet.motors.filter((m) => toNumber(m.kw) != null || m.name).map((m) => ({ name: m.name, kw: toNumber(m.kw) || 0, starter: m.starter, qty: toNumber(m.qty) || 1 }))
  };
}

// API spec / import result -> form state
export function cabinetFromSpec(spec, index = 0) {
  const asText = (value) => (value == null || value === 0 ? '' : String(value));
  return newCabinet(index, {
    name: spec.name || `Cabinet ${index + 1}`,
    kind: spec.kind === 'wall' ? 'wall' : 'floor',
    widthMm: asText(spec.widthMm),
    incomingA: asText(spec.incomingA),
    plcCpu: Boolean(spec.plcCpu),
    ioStations: spec.ioStations == null ? '' : String(spec.ioStations),
    safetyRelays: spec.safetyRelays == null ? '' : String(spec.safetyRelays),
    weighingPoints: asText(spec.weighingPoints),
    io: Object.fromEntries(Object.keys(IO_LABELS).map((type) => [type, asText(spec.io?.[type])])),
    motors: (spec.motors || []).map((m) => ({ key: newKey(), name: m.name || '', kw: m.kw == null ? '' : String(m.kw), starter: m.starter || 'dol', qty: String(m.qty || 1) }))
  });
}

export function sharePointFileUrl(library, folder, fileName) {
  if (!library || !folder || !fileName) return null;
  const path = `${folder}/${fileName}`
    .split('/')
    .map((part) => encodeURIComponent(part))
    .join('/');
  return `https://schuilenburg.sharepoint.com/${encodeURIComponent(library)}${path}?web=1`;
}
